<template>
  <section class="height-100-p" id="shippers-search-summary-section">
    <el-form :model="editForm" ref="editForm" size="mini">
      <el-table
        :data="shipperPage.list"
        size="mini"
        tooltip-effect="dark"
        class="dark-blue-table"
        :empty-text="$t('common.notAbleToFindRecords')"
        stripe
        v-loading="shipperPage.loadingSearch"
        sortable
        @sort-change="handleSortChange"
        :default-sort="{prop: 'shipperNameAndNumber', order: 'ascending'}"
      >
        <el-table-column
          prop="shipperNameAndNumber"
          :label="$t('shipper.shipper')"
          :min-width="12"
          align="left"
          header-align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <el-tooltip class="item" effect="dark" :content="scope.row.shipperNameAndNumber" placement="top">
              <span>{{ scope.row.shipperNameAndNumber }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column
          prop="warehouseCode"
          :label="$t('shipper.warehouse')"
          :min-width="4"
          align="center"
          sortable="custom"
        >
        </el-table-column>
        <el-table-column
          prop="shipperGroup"
          :label="$t('shipper.shipperGroup')"
          :min-width="4"
          align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <template v-if="!scope.row.edit">
              <span>{{ scope.row.shipperGroup }}</span>
            </template>
            <template v-else>
              <shipper-group-select
                ref="shipperGroupSelect"
                :value.sync="editForm.shipperGroupId"
                :includeOptionAll="false"
                :includeOptionNone="true" />
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="shipperCutoffGroup"
          :label="$t('shipper.cutoffGroup')"
          :min-width="4"
          align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <template v-if="!scope.row.edit">
              <span>{{ scope.row.shipperCutoffGroup }}</span>
            </template>
            <template v-else>
              <shipper-cutoff-group
                  ref="shipperCutoffGroupSelect"
                  :value.sync="editForm.shipperCutoffGroupId"
                  :include-option-all="false"
                  :include-option-none="true"/>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="shipperCutoffExempt"
          :label="$t('shipper.cutoffExempt')"
          :min-width="4"
          align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <template v-if="!scope.row.edit">
              <i v-if="scope.row.shipperCutoffExempt" class="el-icon-success green-success"></i>
            </template>
            <template v-else>
              <shipper-cutoff-exempt
                  ref="shipperCutoffExemptSelect"
                  :value.sync="editForm.shipperCutoffExempt"
                  :include-option-all="false"
              ></shipper-cutoff-exempt>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="active"
          :label="$t('shipper.active')"
          :min-width="4"
          align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <i v-if="scope.row.active" class="el-icon-success green-success"></i>
          </template>
        </el-table-column>
        <el-table-column
            :min-width="4"
            :label="$t('common.options')"
            align="center"
            class-name="fixed-width">
          <template slot-scope="scope">
            <el-tooltip
              v-if="showEditButton()"
              class="item el-icon-edit-tooltip"
              effect="dark"
              :enterable="false"
              :content="$t('routing.shuttleSetUp.edit')"
              placement="top-start"
            >
              <i class="el-icon-edit icon-action" @click="handleEdit(scope.row)" />
            </el-tooltip>
            <template v-else-if="scope.row.edit">
              <el-button icon="el-icon-success" circle size="mini" @click="handleUpdate(scope.row)" />
              <el-button icon="el-icon-circle-close" circle size="mini" @click="cancelEdit(scope.row)" />
            </template>
          </template>
        </el-table-column>
      </el-table>
    </el-form>

    <div class="pagination-footer" v-show="shipperPage.total > 0">
      <span class="el-pagination__total">
        <span class="el-pagination__title">{{ $t('common.total') }}</span>
           {{ shipperPage.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="shipperPage.actualFilters.rows"
        :current-page.sync="shipperPage.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="shipperPage.total"
        layout="sizes, prev, pager, next, jumper"
      >
      </el-pagination>
    </div>
  </section>
</template>

<script>
import { Message } from 'element-ui';
import { mapGetters } from 'vuex';
import constants from '@/utils/constants';
import ShipperGroupSelect from '@/components/ShipperGroup/ShipperGroupSelect';
import ShipperCutoffGroup from '@/views/warehouse/shippers/components/ShipperCutoffGroup';
import ShipperCutoffExempt from '@/views/warehouse/shippers/components/ShipperCutoffExempt';

const accountSearchShippersAction = 'account/searchShippers';

export default {
  name: 'shippersSummarySection',
  components: {
    ShipperGroupSelect,
    ShipperCutoffGroup,
    ShipperCutoffExempt,
  },
  computed: {
    ...mapGetters([
      'shipperPage'
    ])
  },
  data() {
    return {
      sizes: constants.TABLES.DEFAULT_LIMIT_SIZES,
      editForm: {
        shipperId: null,
        shipperGroupId: null,
        shipperCutoffGroupId: null,
        shipperCutoffExempt: null,
      }
    };
  },
  methods: {
    handleSearchChangeLimit(val) {
      this.$store.dispatch(accountSearchShippersAction, {
        rows: val,
        page: constants.TABLES.DEFAULT_PAGE
      });
    },
    handleSearchChangePage(val) {
      this.$store.dispatch(accountSearchShippersAction, { page: val });
    },
    handleSortChange({ prop, order }) {
      this.$store.dispatch(accountSearchShippersAction, {
        orderField: prop,
        orderDirection: constants.TABLES.ORDER_DIRECTION[order]
      });
    },
    showEditButton() {
      return this.$can(this.permissions.WAREHOUSE.EDIT_SHIPPERS)
        && !this.shipperPage.isEditingShipper;
    },
    handleEdit(row) {
      this.$store.dispatch('account/setEditingShipper', true);
      row.edit = true;
      this.editForm.shipperId = row.shipperId;
      this.editForm.shipperGroupId = row.shipperGroupId;
      this.editForm.shipperCutoffGroupId = row.shipperCutoffGroupId;
      this.editForm.shipperCutoffExempt = row.shipperCutoffExempt;
      this.$nextTick(() => {
        this.$refs.shipperGroupSelect.setSelectedShipperGroup(this.editForm.shipperGroupId);
        this.$refs.shipperCutoffGroupSelect.setSelectedShipperCutoffGroup(this.editForm.shipperCutoffGroupId);
        this.$refs.shipperCutoffExemptSelect.setSelectedShipperCutoffExempt(this.editForm.shipperCutoffExempt);
      });
    },
    cancelEdit(row) {
      this.$store.dispatch('account/setEditingShipper', false);
      row.edit = false;
    },
    async handleUpdate(row) {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      try {
        Message.closeAll();
        const response = await this.$store.dispatch('account/shipperUpdate', this.editForm);
        this.$message.success(response.message);
        this.cancelEdit(row);
        this.$store.dispatch(accountSearchShippersAction, {});
      } finally {
        loading.close();
      }
    }
  }
};
</script>
